import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, Calculator, CandlestickChart, Calendar, ChevronRight, LayoutGrid, MessageSquare, Menu, Sparkles, Trophy, User, X } from 'lucide-react';
import { Button } from '../atoms/Button';
import { SearchBox } from './SearchBox';
import { Avatar } from '../atoms/Avatar';
import { LevelBadge } from '../atoms/LevelBadge';
import { NumberBadge } from '../atoms/NumberBadge';
import { get } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useNotifStore } from '@/store/notifStore';

const NAV_ITEMS = [
  // [법인 전환 후 활성화] 실시간 시세 기능 — 현재 비활성화
  // { to: '/market', label: '시세' },
  // { to: '/virtual', label: '모의투자' },
  { to: '/calendar', label: '주요일정' },
  { to: '/events', label: '이벤트' },
  { to: '/ranking', label: '랭킹' },
  { to: '/calculator/averaging-down', label: '물타기 계산기' },
  { to: '/chart-patterns', label: '차트 패턴 사전' },
];

/* 드로어 하단 NAV 아이콘 매핑 — 시각적 단서를 더해 단조로움 해소 */
const DRAWER_NAV_ICONS: Record<string, typeof Calendar> = {
  '/calendar': Calendar,
  '/events': Sparkles,
  '/ranking': Trophy,
  '/calculator/averaging-down': Calculator,
  '/chart-patterns': CandlestickChart,
};

/* BoardTabs 와 동일한 정렬/필터 — 햄버거 드로어 게시판 목록도 같은 순서로 노출 */
const DRAWER_PRIORITY_SLUGS = ['diary', 'stock', 'coin', 'free'];
const DRAWER_HIDDEN_SLUGS = new Set(['event', 'notice-ops', 'suggest', 'news']);
/* BoardTabs 와 동일 — 특정 게시판은 전용 페이지로 라우팅 (BoardPage 대신) */
const DRAWER_SPECIAL_TARGETS: Record<string, string> = {
  market: '/market',
};

interface DrawerBoardRow {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  is_visible: boolean;
}

export function Header() {
  const { isLoggedIn, me } = useAuthStore();
  const { setLoginModalOpen, theme } = useUIStore();
  const { unreadCount } = useNotifStore();
  const navigate = useNavigate();
  const location = useLocation();

  /* 모바일 햄버거 드로어 */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* 라우트 변경 시 드로어 자동 닫기 — 메뉴 클릭 후 navigate() 가 일어나도 같은 효과 */
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  /* 드로어 열렸을 때 body 스크롤 잠금 */
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  /* 드로어용 게시판 목록 — BoardTabs 와 같은 queryKey 로 캐시 공유 (네트워크 중복 호출 X) */
  const { data: drawerBoardsData } = useQuery({
    queryKey: ['boards', 'visible'],
    queryFn: () => get<DrawerBoardRow[]>('/boards'),
    staleTime: 5 * 60_000,
  });
  const drawerBoards = useMemo<DrawerBoardRow[]>(() => {
    const all = drawerBoardsData ?? [];
    const visible = all.filter((b) => !DRAWER_HIDDEN_SLUGS.has(b.slug));
    const priority = DRAWER_PRIORITY_SLUGS
      .map((slug) => visible.find((b) => b.slug === slug))
      .filter((b): b is DrawerBoardRow => !!b);
    const rest = visible.filter((b) => !DRAWER_PRIORITY_SLUGS.includes(b.slug));
    return [...priority, ...rest];
  }, [drawerBoardsData]);

  /* 다크 모드는 별도 로고 SVG 사용 (아이콘을 화이트 톤으로 반전).
     light → /favicon.svg / dark → /favicon_dark.svg */
  const logoSrc = theme === 'dark' ? '/favicon_dark.svg' : '/favicon.svg';

  const { data: msgData } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: () => get<{ count: number }>('/messages/unread-count'),
    enabled: isLoggedIn,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const msgUnread = msgData?.count ?? 0;

  /* ─────────────── 공통 버튼들 ─────────────── */

  const messageButton = (
    <button
      type="button"
      onClick={() => navigate('/messages')}
      aria-label="쪽지"
      className="relative inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text)] transition-colors hover:bg-[var(--color-primary)]/10"
    >
      <MessageSquare className="h-5 w-5" />
      {msgUnread > 0 && (
        <span className="absolute -right-0.5 -top-0.5">
          <NumberBadge count={msgUnread} />
        </span>
      )}
    </button>
  );

  const notifButton = (
    <button
      type="button"
      onClick={() => navigate('/notifications')}
      aria-label="알림"
      className="relative inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text)] transition-colors hover:bg-[var(--color-primary)]/10"
    >
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5">
          <NumberBadge count={unreadCount} />
        </span>
      )}
    </button>
  );

  /**
   * 프로필 버튼.
   * - `compact=true` (모바일): 아바타 + 닉네임 (좁은 화면 대비 max-w 짧게 + truncate, 레벨 배지는 숨김)
   * - `compact=false` (PC): 아바타 + 닉네임 + 레벨 배지
   * pl-0 — 아바타가 버튼 좌측 끝에 붙고, hover 배경도 아바타 외곽선 기준에서 시작.
   */
  const profileButton = (compact: boolean) => (
    <button
      onClick={() => navigate('/me')}
      className="flex items-center gap-2 rounded-full pl-0 pr-3 transition-colors hover:bg-[var(--color-primary)]/10"
      aria-label="내 프로필"
    >
      <Avatar
        src={me?.profileImageUrl ?? undefined}
        alt={me?.nickname}
        size="sm"
      />
      {compact ? (
        <span className="block max-w-[72px] truncate text-xs font-semibold sm:max-w-[110px]">
          {me?.nickname}
        </span>
      ) : (
        <>
          <span className="block max-w-[140px] truncate text-xs font-semibold">
            {me?.nickname}
          </span>
          {me && <LevelBadge level={me.level} size="sm" />}
        </>
      )}
    </button>
  );

  const loginButton = (
    <Button variant="primary" size="sm" onClick={() => setLoginModalOpen(true)}>
      <User className="h-4 w-4" />
      로그인
    </Button>
  );

  /* ─────────────── 모바일 햄버거 드로어 (Portal) ───────────────
   * Header 의 backdrop-blur 가 자식 fixed 의 containing block 을 헤더로 묶어
   * "헤더 영역만 덮이는" 버그를 발생시키므로, Portal 로 body 에 직접 렌더한다. */
  const drawerPortal =
    typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`fixed inset-0 z-[100] md:hidden ${drawerOpen ? '' : 'pointer-events-none'}`}
            aria-hidden={!drawerOpen}
          >
            {/* 배경 오버레이 */}
            <button
              type="button"
              aria-label="메뉴 닫기"
              onClick={() => setDrawerOpen(false)}
              className={`absolute inset-0 bg-slate-900/55 backdrop-blur-sm transition-opacity duration-200 ${
                drawerOpen ? 'opacity-100' : 'opacity-0'
              }`}
            />

            {/* 우측 슬라이드 패널 */}
            <aside
              role="dialog"
              aria-modal="true"
              aria-label="전체 메뉴"
              className={`absolute right-0 top-0 flex h-full w-80 max-w-[88%] flex-col overflow-hidden bg-[var(--color-surface)] shadow-2xl transition-transform duration-300 ease-out ${
                drawerOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              {/* 패널 헤더 — 사용자 프로필 또는 로그인 유도 */}
              <div className="relative bg-gradient-to-br from-[var(--color-primary)] to-[#2a4f7a] px-5 pb-5 pt-4 text-white">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="메뉴 닫기"
                  className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/15 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>

                {isLoggedIn && me ? (
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/me');
                      setDrawerOpen(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl pr-10 text-left"
                  >
                    <Avatar src={me.profileImageUrl ?? undefined} alt={me.nickname} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="block max-w-[140px] truncate text-base font-extrabold">
                          {me.nickname}
                        </span>
                        <LevelBadge level={me.level} size="sm" />
                      </div>
                      <p className="mt-0.5 text-xs text-white/80">내 프로필 · 설정</p>
                    </div>
                  </button>
                ) : (
                  <div className="pr-10">
                    <p className="text-base font-extrabold">시세공간에 오신 걸 환영해요</p>
                    <p className="mt-1 text-xs text-white/85">로그인하고 포인트도 모아 보세요</p>
                    <button
                      type="button"
                      onClick={() => {
                        setLoginModalOpen(true);
                        setDrawerOpen(false);
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-bold text-[var(--color-primary)] shadow-sm transition-colors hover:bg-white/90"
                    >
                      <User className="h-3.5 w-3.5" />
                      로그인
                    </button>
                  </div>
                )}
              </div>

              {/* 본문 — 스크롤 영역 */}
              <div className="flex-1 overflow-y-auto bg-[var(--color-bg)]">
                {/* 상단: 게시판 — 가독성 위해 카드 스타일 + 핀 색상 토큰 */}
                <div className="px-3 pb-2 pt-4">
                  <div className="mb-2 flex items-center gap-2 px-2">
                    <LayoutGrid className="h-3.5 w-3.5 text-[var(--color-text-sub)]" />
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                      게시판
                    </p>
                  </div>
                  <ul className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                    <li>
                      <NavLink
                        to="/board/popular"
                        onClick={() => setDrawerOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3 text-sm transition-colors last:border-b-0 ${
                            isActive
                              ? 'bg-[var(--color-primary)]/10 font-semibold text-[var(--color-primary)]'
                              : 'text-[var(--color-text)] hover:bg-[var(--color-primary)]/5'
                          }`
                        }
                      >
                        <span className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-amber-500" />
                          인기
                        </span>
                        <ChevronRight className="h-4 w-4 text-[var(--color-text-sub)]" />
                      </NavLink>
                    </li>
                    {drawerBoards.map((b) => (
                      <li key={b.slug}>
                        <NavLink
                          to={DRAWER_SPECIAL_TARGETS[b.slug] ?? `/board/${b.slug}`}
                          onClick={() => setDrawerOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3 text-sm transition-colors last:border-b-0 ${
                              isActive
                                ? 'bg-[var(--color-primary)]/10 font-semibold text-[var(--color-primary)]'
                                : 'text-[var(--color-text)] hover:bg-[var(--color-primary)]/5'
                            }`
                          }
                        >
                          <span>{b.name}</span>
                          <ChevronRight className="h-4 w-4 text-[var(--color-text-sub)]" />
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 하단: 주요일정 / 이벤트 / 랭킹 */}
                <div className="px-3 pb-6 pt-4">
                  <div className="mb-2 flex items-center gap-2 px-2">
                    <Menu className="h-3.5 w-3.5 text-[var(--color-text-sub)]" />
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                      바로가기
                    </p>
                  </div>
                  <ul className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                    {NAV_ITEMS.map((item) => {
                      const Icon = DRAWER_NAV_ICONS[item.to] ?? Menu;
                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            onClick={() => setDrawerOpen(false)}
                            className={({ isActive }) =>
                              `flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3 text-sm transition-colors last:border-b-0 ${
                                isActive
                                  ? 'bg-[var(--color-primary)]/10 font-semibold text-[var(--color-primary)]'
                                  : 'text-[var(--color-text)] hover:bg-[var(--color-primary)]/5'
                              }`
                            }
                          >
                            <span className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-[var(--color-primary)]" />
                              {item.label}
                            </span>
                            <ChevronRight className="h-4 w-4 text-[var(--color-text-sub)]" />
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </aside>
          </div>,
          document.body,
        )
      : null;

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur">
      {/* ══════════════════════════════════════════════════════════════
       *  PC (md+) — 1줄: 로고 · 검색창(320px) · 쪽지 · 알림 · 프로필(닉네임·레벨 포함)
       *  2줄: 메인 네비 (주요일정/이벤트/랭킹)
       * ══════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-5">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <img
              src={logoSrc}
              alt="시세공간"
              width={32}
              height={32}
              className="h-8 w-8"
            />
            <span className="text-2xl font-semibold text-[var(--color-text)]">시세공간</span>
          </Link>

          <div className="ml-4 w-[320px]">
            <SearchBox size="sm" />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1">
            {isLoggedIn ? (
              <>
                {profileButton(false)}
                {messageButton}
                {notifButton}
              </>
            ) : (
              loginButton
            )}
          </div>
        </div>

        <nav className="border-t border-[var(--color-border)]">
          <div className="mx-auto flex max-w-6xl items-center gap-6 px-5 py-2 text-sm font-semibold">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `transition-colors duration-150 ${
                    isActive
                      ? 'text-[var(--color-text)]'
                      : 'text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      {/* ══════════════════════════════════════════════════════════════
       *  모바일 (md 미만) — 2줄:
       *    1줄: [로고] ... [프로필][쪽지][알림]
       *    2줄: [          검색창 전체 너비          ]
       *  모바일 네비는 하단 BottomNav 가 대체
       * ══════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* 1줄 — 로고 + 우측 아이콘 클러스터 */}
        <div className="mx-auto flex h-14 items-center justify-between px-4">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <img
              src={logoSrc}
              alt="시세공간"
              width={28}
              height={28}
              className="h-7 w-7"
            />
            <span className="text-xl font-semibold text-[var(--color-primary)]">시세공간</span>
          </Link>

          <div className="flex shrink-0 items-center gap-1">
            {isLoggedIn ? (
              <>
                {profileButton(true)}
                {messageButton}
                {notifButton}
              </>
            ) : (
              loginButton
            )}
            {/* 햄버거 — 모바일 우측 끝, 데스크탑 미노출(부모 div 가 md:hidden) */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="메뉴 열기"
              aria-expanded={drawerOpen}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text)] transition-colors hover:bg-[var(--color-primary)]/10"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 2줄 — 검색창 전체 너비. 포커스 시 SearchBox 내부 드롭다운(자동완성/인기) 노출 */}
        <div className="border-t border-[var(--color-border)] px-3 py-2">
          <SearchBox size="sm" placeholder="종목, 게시글을 검색하세요" />
        </div>
      </div>

      {/* 모바일 햄버거 드로어 — Portal 로 body 직속 렌더.
          Header 가 backdrop-blur 를 사용하면 그 컨테이닝 블록 안에 fixed 가 갇히는
          CSS 명세 동작 때문에, drawer 를 header 외부(body)로 분리 마운트한다. */}
      {drawerPortal}
    </header>
  );
}
